import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CrackedLogo } from "../components/CrackedLogo";
import { 
  CheckCircle2, 
  Calendar, 
  Clock, 
  Video, 
  Copy, 
  Check,
  Users,
  CalendarPlus
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

// Helper to format time from ISO string (times are already in IST from backend)
const formatTimeIST = (isoString) => {
  const parts = isoString.split('T');
  if (parts.length < 2) return isoString;
  
  const timeParts = parts[1].split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${hours}:${minutes} ${ampm}`;
};

// Helper to format date from ISO string (dates are in IST from backend)
const formatDateIST = (isoString) => {
  const parts = isoString.split('T');
  if (parts.length < 1) return isoString;
  
  const dateParts = parts[0].split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const day = parseInt(dateParts[2], 10);
  
  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

export default function BookingConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const booking = location.state?.booking;
  const interviewer = location.state?.interviewer;

  useEffect(() => {
    if (!booking) {
      navigate('/book');
    }
  }, [booking, navigate]);

  if (!booking) {
    return null;
  }

  const copyMeetLink = () => {
    if (booking.meet_link) {
      navigator.clipboard.writeText(booking.meet_link);
      setCopied(true);
      toast.success("Meet link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-cracked-deep-teal text-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <CrackedLogo inverted />
          <button
            onClick={() => {
              localStorage.removeItem('cracked_user_id');
              window.location.href = '/';
            }}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title="Logout"
            data-testid="logout-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-12">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-cracked-deep-teal mb-2">Booking Confirmed!</h1>
          <p className="text-cracked-deep-teal/60">
            Calendar invites have been sent to all attendees
          </p>
        </div>

        <Card className="animate-fade-in stagger-1" data-testid="confirmation-card">
          <CardContent className="p-6 space-y-6">
            {/* Interviewer Info */}
            <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
              <div className="w-14 h-14 rounded-full bg-cracked-light-teal/10 flex items-center justify-center">
                {interviewer?.picture ? (
                  <img src={interviewer.picture} alt={interviewer.name} className="w-14 h-14 rounded-full" />
                ) : (
                  <Users className="w-7 h-7 text-cracked-light-teal" />
                )}
              </div>
              <div>
                <p className="text-sm text-cracked-deep-teal/60">Interview with</p>
                <p className="font-semibold text-lg text-cracked-deep-teal">{booking.interviewer_name}</p>
              </div>
            </div>

            {/* Date & Time */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-cracked-deep-teal">
                <div className="w-10 h-10 rounded-lg bg-cracked-light-teal/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-cracked-light-teal" />
                </div>
                <div>
                  <p className="font-medium">{formatDateIST(booking.slot_start)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-cracked-deep-teal">
                <div className="w-10 h-10 rounded-lg bg-cracked-light-teal/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-cracked-light-teal" />
                </div>
                <div>
                  <p className="font-medium">{formatTimeIST(booking.slot_start)} - {formatTimeIST(booking.slot_end)}</p>
                </div>
              </div>
            </div>

            {/* Interviewee */}
            {booking.additional_emails?.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-medium text-cracked-deep-teal mb-2">Interviewee</p>
                <div className="space-y-1">
                  {booking.additional_emails.map((email, index) => (
                    <p key={index} className="text-sm text-cracked-deep-teal/60">{email}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Meet Link */}
            {booking.meet_link && (
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-cracked-deep-teal flex items-center gap-2">
                    <Video className="w-4 h-4 text-cracked-light-teal" />
                    Google Meet Link
                  </p>
                  <button
                    onClick={copyMeetLink}
                    className={`copy-btn p-2 rounded-lg hover:bg-slate-100 transition-colors ${copied ? 'copied' : ''}`}
                    data-testid="copy-meet-link-btn"
                  >
                    {copied ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5 text-cracked-deep-teal/60" />
                    )}
                  </button>
                </div>
                <a
                  href={booking.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 bg-cracked-light-teal/5 rounded-lg border border-cracked-light-teal/20 text-cracked-light-teal hover:bg-cracked-light-teal/10 transition-colors text-sm font-mono break-all"
                  data-testid="meet-link"
                >
                  {booking.meet_link}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4 mt-8 animate-fade-in stagger-2">
          <Button
            variant="outline"
            onClick={() => navigate('/book')}
            className="flex-1"
            data-testid="book-another-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Book Another Slot
          </Button>
        </div>
      </main>
    </div>
  );
}
